package ai.jessie.web.projects

import ai.jessie.shared.projects.PositionInput
import ai.jessie.shared.projects.ProjectResponse
import ai.jessie.shared.projects.SaveProjectRequest

// Editable text as entered. Amounts stay exact decimal text; the client never computes money.
data class ProjectDraft(
    val name: String = "",
    val description: String = "",
    val customerId: String = "",
    val billable: Boolean = true,
    val active: Boolean = true,
    val positions: List<PositionDraft> = emptyList(),
)

// Position IDs are kept so that saved positions keep their identity.
data class PositionDraft(
    val id: String? = null,
    val name: String = "",
    val type: String = "",
    val billable: Boolean = true,
    val active: Boolean = true,
    val unitPrice: String = "",
    val vatRateId: String = "",
)

internal fun ProjectResponse.draft() =
    ProjectDraft(
        name = name,
        description = description.orEmpty(),
        customerId = customerId.orEmpty(),
        billable = billable,
        active = active,
        positions =
            positions.map {
                with(it.details) {
                    PositionDraft(
                        id,
                        name,
                        type,
                        billable,
                        active,
                        unitPrice.orEmpty(),
                        vatRateId.orEmpty(),
                    )
                }
            },
    )

// The server validates; the client only trims and sends an empty optional value as absent.
// BR-052: a non-billable position has neither price nor VAT, so its hidden values are not sent.
internal fun ProjectDraft.request(expectedUpdatedAt: String?) =
    SaveProjectRequest(
        name = name.trim(),
        billable = billable,
        positions =
            positions.map {
                PositionInput(
                    name = it.name.trim(),
                    type = it.type,
                    billable = it.billable,
                    active = it.active,
                    unitPrice = it.unitPrice.optional().takeIf { _ -> it.billable },
                    vatRateId = it.vatRateId.optional().takeIf { _ -> it.billable },
                    id = it.id,
                )
            },
        description = description.optional(),
        customerId = customerId.optional(),
        active = active,
        expectedUpdatedAt = expectedUpdatedAt,
    )

// A6, A7: only absence is checked here; lengths, amounts, customer and VAT validity are the
// server's decision.
internal fun ProjectDraft.missingFields(): List<String> = buildList {
    if (name.isBlank()) add("name")
    if (billable && customerId.isBlank()) add("customerId")
    if (positions.isEmpty()) add("positions")
    positions.forEachIndexed { index, position ->
        val prefix = "positions[$index]"
        if (position.name.isBlank()) add("$prefix.name")
        if (position.type !in positionTypes) add("$prefix.type")
        if (position.billable && position.unitPrice.isBlank()) add("$prefix.unitPrice")
        if (position.billable && position.vatRateId.isBlank()) add("$prefix.vatRateId")
    }
}

private fun String.optional() = trim().ifEmpty { null }
